import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import { Menu, X } from "lucide-react";

const LINKS = [
  { id: "filosofia", label: "Filosofia", testid: "nav-link-philosophy" },
  { id: "servizi", label: "Servizi & Prezzi", testid: "nav-link-services" },
  { id: "galleria", label: "Il Locale", testid: "nav-link-gallery" },
  { id: "recensioni", label: "Recensioni", testid: "nav-link-reviews" },
  { id: "prenota", label: "Prenota", testid: "nav-link-booking" },
  { id: "contatti", label: "Contatti", testid: "nav-link-contacts" },
];

export const scrollTo = (id) => {
  const el = document.getElementById(id);
  if (!el) return;
  if (window.__lenis) window.__lenis.scrollTo(el, { offset: -70 });
  else el.scrollIntoView({ behavior: "smooth", block: "start" });
};

export function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (id) => {
    setMenuOpen(false);
    scrollTo(id);
  };

  return (
    <motion.header
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.8, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
        scrolled || menuOpen ? "backdrop-blur-xl bg-void/85 border-b border-white/[0.08]" : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8 h-16 sm:h-20 flex items-center justify-between">
        <button
          data-testid="nav-brand-logo"
          onClick={() => {
            setMenuOpen(false);
            window.scrollTo({ top: 0, behavior: "smooth" });
          }}
          className="flex items-center gap-3 group"
        >
          <img src="/logo.png" alt="Modico Barberia" className="h-10 w-auto object-contain transition-transform duration-300 group-hover:scale-105" />
          <span className="font-serif text-lg sm:text-xl tracking-wide text-ivory whitespace-nowrap">
            Modico <span className="italic text-ember">Barberia</span>
          </span>
        </button>

        <nav className="hidden lg:flex items-center gap-8">
          {LINKS.map((l) => (
            <button
              key={l.id}
              data-testid={l.testid}
              onClick={() => scrollTo(l.id)}
              className="text-xs font-mono uppercase tracking-widest text-sand hover:text-ember transition-colors duration-300"
            >
              {l.label}
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <Link
            to="/admin"
            data-testid="nav-link-admin"
            className="hidden sm:block text-xs font-mono uppercase tracking-widest text-stone hover:text-sand transition-colors duration-300"
          >
            Area Riservata
          </Link>
          <button
            data-testid="nav-booking-cta"
            onClick={() => go("prenota")}
            className="hidden sm:block relative px-4 sm:px-5 py-2.5 text-xs font-mono uppercase tracking-widest text-white bg-ember hover:bg-ember-hover rounded-sm transition-colors duration-300 shadow-[0_0_24px_rgba(230,90,30,0.25)]"
          >
            Prenota Ora
          </button>
          <button
            data-testid="nav-mobile-menu-button"
            onClick={() => setMenuOpen((v) => !v)}
            aria-label={menuOpen ? "Chiudi menu" : "Apri menu"}
            className="lg:hidden p-2.5 rounded-md border border-white/10 text-sand hover:text-ember hover:border-ember/40 transition-colors duration-300"
          >
            {menuOpen ? <X size={18} /> : <Menu size={18} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {menuOpen && (
          <motion.nav
            data-testid="nav-mobile-menu"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
            className="lg:hidden overflow-hidden bg-void/95 backdrop-blur-xl border-b border-white/[0.08]"
          >
            <div className="px-5 py-4 flex flex-col">
              {LINKS.map((l, i) => (
                <button
                  key={l.id}
                  data-testid={`nav-mobile-${l.id}`}
                  onClick={() => go(l.id)}
                  className={`text-left font-serif text-2xl text-ivory py-3.5 hover:text-ember transition-colors duration-300 ${
                    i > 0 ? "border-t border-white/[0.06]" : ""
                  }`}
                >
                  {l.label}
                </button>
              ))}
              <Link
                to="/admin"
                data-testid="nav-mobile-admin"
                onClick={() => setMenuOpen(false)}
                className="text-left font-mono text-xs uppercase tracking-widest text-stone py-3.5 border-t border-white/[0.06] hover:text-ember transition-colors duration-300"
              >
                Area Riservata
              </Link>
              <button
                data-testid="nav-mobile-booking-cta"
                onClick={() => go("prenota")}
                className="mt-4 mb-2 w-full py-4 text-xs font-mono uppercase tracking-widest text-white bg-ember hover:bg-ember-hover rounded-sm transition-colors duration-300 shadow-[0_0_24px_rgba(230,90,30,0.25)]"
              >
                Prenota Ora
              </button>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </motion.header>
  );
}
